import { iconTypes } from "skedyul-ui/src/components/ui/Icon";
import type { NavigationEditorItem } from "skedyul-ui/src/components/ui/RestructurableNavigationLayout";

import type {
  SidebarGroupProps,
  SidebarSectionProps,
} from "skedyul-ui/src/components/ui/SideNavigationV2/SideBar/navigation";

export type WorkplaceNavigationItem = {
  id: string;
  url: string;
  label?: string | null;
  icon?: string | null;
  sortIndex: number;
  parentId?: string | null;
  page?: {
    id: string;
    title: string;
    icon: string | null;
    path: string;
  } | null;
  children?: WorkplaceNavigationItem[];
};

function isGroupUrl(url: string): boolean {
  return url.startsWith("_group/");
}

function isSectionUrl(url: string): boolean {
  return url.startsWith("_section/");
}

function mapLeafItem(
  item: NavigationEditorItem,
  parentId: string | null,
  subdomain: string,
  isIncludesActive: (path: string) => boolean,
) {
  const pagePath = item.pagePath ?? item.url;
  return {
    label: item.label || pagePath || "Unknown Page",
    href: `/${subdomain}/${item.url || pagePath}`,
    icon: (item.icon || "Text") as iconTypes,
    isActive: isIncludesActive(`/${pagePath}`),
    navigation: {
      id: item.id,
      parentId,
      kind: "link" as const,
      label: item.label || pagePath || "Unknown Page",
      icon: item.icon || "Text",
    },
  };
}

function mapCollapsibleGroup(
  item: NavigationEditorItem,
  parentId: string | null,
  subdomain: string,
  isIncludesActive: (path: string) => boolean,
): SidebarGroupProps {
  const entries: NonNullable<SidebarGroupProps["entries"]> = []
  const childItems: ReturnType<typeof mapLeafItem>[] = []
  const childGroups: SidebarGroupProps[] = []

  for (const child of item.children ?? []) {
    if (child.kind === "group" || isGroupUrl(child.url)) {
      const nestedGroup = mapCollapsibleGroup(
        child,
        item.id,
        subdomain,
        isIncludesActive,
      )
      childGroups.push(nestedGroup)
      entries.push({ kind: "group", group: nestedGroup })
      continue
    }

    if (child.kind === "link" || child.pagePath) {
      const leaf = mapLeafItem(child, item.id, subdomain, isIncludesActive)
      childItems.push(leaf)
      entries.push({ kind: "link", item: leaf })
    }
  }

  return {
    collapsible: {
      label: item.label || "Group",
      icon: (item.icon && item.icon !== "ChevronDown"
        ? item.icon
        : "FileText") as iconTypes,
    },
    navigation: {
      id: item.id,
      parentId,
      kind: "group",
      label: item.label || "Group",
      icon:
        item.icon && item.icon !== "ChevronDown" ? item.icon : "FileText",
    },
    items: childItems,
    ...(childGroups.length > 0 ? { groups: childGroups } : {}),
    ...(entries.length > 0 ? { entries } : {}),
  };
}

function mapSectionNode(
  item: NavigationEditorItem,
  subdomain: string,
  isIncludesActive: (path: string) => boolean,
): SidebarSectionProps {
  const groups: SidebarGroupProps[] = [];
  let flatLinkBatch: NonNullable<SidebarGroupProps["items"]> = [];

  const flushFlatLinks = () => {
    if (!flatLinkBatch.length) return;
    groups.push({ items: [...flatLinkBatch] });
    flatLinkBatch = [];
  };

  for (const child of item.children ?? []) {
    if (child.kind === "group" || isGroupUrl(child.url)) {
      flushFlatLinks();
      groups.push(
        mapCollapsibleGroup(child, item.id, subdomain, isIncludesActive),
      );
      continue;
    }

    if (child.kind === "link" || child.pagePath) {
      flatLinkBatch.push(
        mapLeafItem(child, item.id, subdomain, isIncludesActive),
      );
    }
  }

  flushFlatLinks();

  return {
    label: item.label || "Home",
    navigation: {
      id: item.id,
      parentId: null,
      kind: "section",
      label: item.label || "Home",
      icon: item.icon ?? undefined,
    },
    groups,
  };
}

export function buildWorkplaceNavigationSectionsFromEditorTree(
  navigationItems: NavigationEditorItem[],
  subdomain: string,
  isIncludesActive: (path: string) => boolean,
): SidebarSectionProps[] {
  if (!navigationItems.length) return [];

  const sections: SidebarSectionProps[] = [];

  for (const item of navigationItems) {
    if (item.kind === "section" || isSectionUrl(item.url)) {
      sections.push(mapSectionNode(item, subdomain, isIncludesActive));
      continue;
    }

    if (item.kind === "group" || isGroupUrl(item.url)) {
      sections.push({
        label: "",
        groups: [mapCollapsibleGroup(item, null, subdomain, isIncludesActive)],
      });
      continue;
    }

    if (item.kind === "link" || item.pagePath) {
      sections.push({
        label: "",
        groups: [
          {
            items: [mapLeafItem(item, null, subdomain, isIncludesActive)],
          },
        ],
      });
    }
  }

  return sections;
}

export function buildWorkplaceNavigationSections(
  navigationItems: WorkplaceNavigationItem[] | undefined,
  subdomain: string,
  isIncludesActive: (path: string) => boolean,
): SidebarSectionProps[] {
  if (!navigationItems?.length) return [];

  const editorItems: NavigationEditorItem[] = navigationItems.map((item) => {
    const kind: NavigationEditorItem["kind"] = isSectionUrl(item.url)
      ? "section"
      : isGroupUrl(item.url)
        ? "group"
        : "link";

    return {
      id: item.id,
      url: item.url,
      label: item.label || item.page?.title || item.url,
      icon: item.icon,
      sortIndex: item.sortIndex,
      parentId: item.parentId ?? null,
      pagePath: item.page?.path ?? null,
      kind,
      children: item.children
        ? buildWorkplaceNavigationEditorTree(item.children)
        : undefined,
    };
  });

  return buildWorkplaceNavigationSectionsFromEditorTree(
    editorItems,
    subdomain,
    isIncludesActive,
  );
}

function buildWorkplaceNavigationEditorTree(
  items: WorkplaceNavigationItem[],
): NavigationEditorItem[] {
  return items.map((item) => {
    const kind: NavigationEditorItem["kind"] = isSectionUrl(item.url)
      ? "section"
      : isGroupUrl(item.url)
        ? "group"
        : "link";

    return {
      id: item.id,
      url: item.url,
      label: item.label || item.page?.title || item.url,
      icon: item.icon,
      sortIndex: item.sortIndex,
      parentId: item.parentId ?? null,
      pagePath: item.page?.path ?? null,
      kind,
      children: item.children
        ? buildWorkplaceNavigationEditorTree(item.children)
        : undefined,
    };
  });
}
